/**
 * Administration.
 *
 * Every mutation below calls an admin-only endpoint. The UI is organised in tabs
 * but grants nothing: a candidate reaching this route by URL gets 403 from the
 * API on each request, and the route guard sends them to the forbidden page.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  FileWarning,
  Settings,
  ShieldAlert,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge, PublicationBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, StatTile } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui/Input';
import { FullPageSpinner, Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { formatDateTime, formatPercent, roleLabel } from '@/lib/format';
import { api, ApiError } from '@/services/api';
import { fetchers, queryKeys } from '@/services/queries';
import type { UserRole } from '@/types/api';

const TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: BarChart3 },
  { id: 'content', label: 'Contenus', icon: BookOpen },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'enrollments', label: 'Inscriptions', icon: ClipboardCheck },
  { id: 'results', label: 'Résultats', icon: CheckCircle2 },
  { id: 'audit', label: 'Journal d’audit', icon: Activity },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function AdminPage() {
  const [tab, setTab] = useState<TabId>('overview');

  return (
    <div className="space-y-6">
      <header>
        <Badge tone="ai" className="mb-3">
          <Settings className="h-3 w-3" />
          Administration
        </Badge>
        <h1 className="text-2xl font-bold tracking-tight">Administration de la plateforme</h1>
        <p className="mt-1 text-sm text-muted">
          Contenus, publication, utilisateurs, rôles, résultats et traçabilité.
        </p>
      </header>

      <div role="tablist" aria-label="Sections d'administration" className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={cn(
              '-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === id
                ? 'border-tech text-tech'
                : 'border-transparent text-muted hover:text-fg',
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? <OverviewTab /> : null}
      {tab === 'content' ? <ContentTab /> : null}
      {tab === 'users' ? <UsersTab /> : null}
      {tab === 'enrollments' ? <EnrollmentsTab /> : null}
      {tab === 'results' ? <ResultsTab /> : null}
      {tab === 'audit' ? <AuditTab /> : null}
    </div>
  );
}

function OverviewTab() {
  const stats = useQuery({ queryKey: queryKeys.adminStats, queryFn: fetchers.adminStats });

  if (stats.isLoading) return <FullPageSpinner label="Chargement des statistiques…" />;
  if (!stats.data) return <Alert tone="danger">Statistiques indisponibles.</Alert>;

  const data = stats.data;

  return (
    <div className="space-y-6">
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Utilisateurs"
          value={data.users_total}
          hint={`${data.users_by_role.candidate ?? 0} candidats · ${data.users_by_role.instructor ?? 0} formateurs`}
          icon={<Users className="h-4 w-4" />}
          accent="tech"
        />
        <StatTile
          label="Modules publiés"
          value={`${data.modules_published}/${data.modules_total}`}
          hint={`${data.modules_in_preparation} en préparation`}
          icon={<BookOpen className="h-4 w-4" />}
          accent="ai"
        />
        <StatTile
          label="Heures publiées"
          value={`${data.content_hours_published} h`}
          hint={`sur ${data.content_hours_total} h au catalogue`}
          icon={<BarChart3 className="h-4 w-4" />}
          accent="agent"
        />
        <StatTile
          label="Score moyen"
          value={formatPercent(data.average_score_percent)}
          hint={`${data.attempts_passed}/${data.attempts_total} tentatives réussies`}
          icon={<CheckCircle2 className="h-4 w-4" />}
          accent="neutral"
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle as="h2">État du contenu pédagogique</CardTitle>
          </CardHeader>
          <CardBody>
            <dl className="space-y-3 text-sm">
              {[
                ['Parcours', data.tracks_total],
                ['Modules au catalogue', data.modules_total],
                ['Chapitres rédigés', `${data.chapters_published}/${data.chapters_total} publiés`],
                ['Travaux pratiques', data.labs_total],
                ['TP avec correction formateur', `${data.labs_with_solution}/${data.labs_total}`],
                ['Quiz', data.quizzes_total],
                ['Questions de quiz', data.quiz_questions_total],
                ['Inscriptions actives', data.enrollments_active],
              ].map(([label, value]) => (
                <div key={String(label)} className="flex items-center justify-between gap-3">
                  <dt className="text-muted">{label}</dt>
                  <dd className="font-semibold tabular-nums text-fg">{value}</dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2">Répartition des rôles</CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="space-y-3">
              {Object.entries(data.users_by_role).map(([role, count]) => {
                const percent = data.users_total ? (count / data.users_total) * 100 : 0;
                return (
                  <li key={role}>
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-muted">{roleLabel(role)}</span>
                      <span className="font-semibold tabular-nums text-fg">{count}</span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-2">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          role === 'admin' ? 'bg-ai' : role === 'instructor' ? 'bg-tech' : 'bg-agent',
                        )}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function ContentTab() {
  const queryClient = useQueryClient();
  const readiness = useQuery({
    queryKey: queryKeys.adminReadiness,
    queryFn: fetchers.adminReadiness,
  });
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const publish = useMutation({
    mutationFn: ({ code, status }: { code: string; status: string }) =>
      api.put(`/admin/modules/${code}/publication`, { status }),
    onSuccess: (_data, variables) => {
      setMessage({
        tone: 'success',
        text: `Module ${variables.code} : statut mis à jour (${variables.status}).`,
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminReadiness });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tracks });
    },
    onError: (error) => {
      setMessage({
        tone: 'danger',
        text:
          error instanceof ApiError
            ? error.message
            : 'La modification du statut a échoué.',
      });
    },
  });

  if (readiness.isLoading) return <FullPageSpinner label="Analyse du contenu…" />;
  const rows = readiness.data ?? [];
  const ready = rows.filter((row) => row.ready_to_publish && row.status !== 'published');

  return (
    <div className="space-y-6">
      {message ? (
        <Alert tone={message.tone === 'success' ? 'success' : 'danger'}>{message.text}</Alert>
      ) : null}

      <Alert tone="info" title="Publication contrôlée">
        Un module ne peut pas être publié sans chapitre publié : l'API refuse l'opération. Cela
        garantit qu'aucun module annoncé comme disponible n'est vide.
      </Alert>

      {ready.length > 0 ? (
        <Alert tone="success" title={`${ready.length} module(s) prêt(s) à publier`}>
          {ready.map((row) => row.code).join(', ')}
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle as="h2">Avancement de production des 28 modules</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Module</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Statut</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Chap.</th>
                  <th className="px-4 py-2.5 text-right font-semibold">TP</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Quiz</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Points bloquants</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.code} className="border-t border-border">
                    <td className="px-4 py-2.5">
                      <p className="font-mono text-xs font-bold text-tech">{row.code}</p>
                      <p className="max-w-xs truncate text-xs text-muted">{row.title}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      <PublicationBadge status={row.status} />
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">
                      {row.chapters_published}/{row.chapters}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">{row.labs}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">{row.quizzes}</td>
                    <td className="px-4 py-2.5">
                      {row.blocking.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-ok">
                          <CheckCircle2 className="h-3 w-3" />
                          Prêt
                        </span>
                      ) : (
                        <span className="inline-flex items-start gap-1 text-xs text-warn">
                          <FileWarning className="mt-0.5 h-3 w-3 shrink-0" />
                          {row.blocking.join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {row.status === 'published' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            publish.mutate({ code: row.code, status: 'in_preparation' })
                          }
                          disabled={publish.isPending}
                        >
                          Masquer
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => publish.mutate({ code: row.code, status: 'published' })}
                          disabled={publish.isPending || !row.ready_to_publish}
                          title={
                            row.ready_to_publish
                              ? 'Publier ce module'
                              : 'Publication impossible : ' + row.blocking.join(', ')
                          }
                        >
                          Publier
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function UsersTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    role: 'candidate' as UserRole,
    password: '',
  });

  const users = useQuery({
    queryKey: queryKeys.adminUsers({ role: roleFilter || undefined, search: search || undefined }),
    queryFn: () =>
      fetchers.adminUsers({
        ...(roleFilter ? { role: roleFilter } : {}),
        ...(search ? { search } : {}),
      }),
  });

  const createUser = useMutation({
    mutationFn: () => api.post('/admin/users', form),
    onSuccess: () => {
      setMessage({ tone: 'success', text: `Compte ${form.email} créé.` });
      setForm({ email: '', full_name: '', role: 'candidate', password: '' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
    onError: (error) =>
      setMessage({
        tone: 'danger',
        text: error instanceof ApiError ? error.message : 'Création impossible.',
      }),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/admin/users/${id}`, patch),
    onSuccess: () => {
      setMessage({ tone: 'success', text: 'Utilisateur mis à jour. Ses sessions sont révoquées.' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error) =>
      setMessage({
        tone: 'danger',
        text: error instanceof ApiError ? error.message : 'Mise à jour impossible.',
      }),
  });

  return (
    <div className="space-y-6">
      {message ? (
        <Alert tone={message.tone === 'success' ? 'success' : 'danger'}>{message.text}</Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle as="h2" className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-tech" />
            Créer un compte
          </CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
            onSubmit={(event) => {
              event.preventDefault();
              createUser.mutate();
            }}
          >
            <Input
              label="Adresse e-mail"
              type="email"
              required
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
            <Input
              label="Nom complet"
              required
              value={form.full_name}
              onChange={(event) => setForm({ ...form, full_name: event.target.value })}
            />
            <Select
              label="Rôle"
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value as UserRole })}
            >
              <option value="candidate">Candidat</option>
              <option value="instructor">Formateur</option>
              <option value="admin">Administrateur</option>
            </Select>
            <Input
              label="Mot de passe initial"
              type="password"
              required
              hint="12 caractères minimum, à changer à la première connexion"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
            <div className="sm:col-span-2 lg:col-span-4">
              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending ? <Spinner /> : <UserPlus className="h-4 w-4" />}
                Créer le compte
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-wrap items-end justify-between gap-3">
          <CardTitle as="h2">Comptes ({users.data?.length ?? 0})</CardTitle>
          <div className="flex flex-wrap items-end gap-3">
            <Input
              type="search"
              placeholder="Rechercher…"
              aria-label="Rechercher un utilisateur"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Select
              aria-label="Filtrer par rôle"
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <option value="">Tous les rôles</option>
              <option value="candidate">Candidats</option>
              <option value="instructor">Formateurs</option>
              <option value="admin">Administrateurs</option>
            </Select>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {users.isLoading ? (
            <p className="px-5 py-6 text-sm text-muted">Chargement…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-2">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Utilisateur</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Rôle</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Parcours</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Dernière connexion</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(users.data ?? []).map((user) => (
                    <tr key={user.id} className="border-t border-border">
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-fg">{user.full_name}</p>
                        <p className="text-xs text-faint">{user.email}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <Select
                          aria-label={`Rôle de ${user.full_name}`}
                          value={user.role}
                          onChange={(event) =>
                            updateUser.mutate({
                              id: user.id,
                              patch: { role: event.target.value },
                            })
                          }
                          className="w-36 py-1 text-xs"
                        >
                          <option value="candidate">Candidat</option>
                          <option value="instructor">Formateur</option>
                          <option value="admin">Administrateur</option>
                        </Select>
                      </td>
                      <td className="px-4 py-2.5">
                        {user.enrolled_track_codes.length > 0 ? (
                          <span className="flex gap-1">
                            {user.enrolled_track_codes.map((code) => (
                              <Badge key={code} tone="neutral">
                                {code}
                              </Badge>
                            ))}
                          </span>
                        ) : (
                          <span className="text-xs text-faint">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted">
                        {formatDateTime(user.last_login_at)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            updateUser.mutate({
                              id: user.id,
                              patch: { is_active: !user.is_active },
                            })
                          }
                        >
                          {user.is_active ? (
                            <>
                              <XCircle className="h-3.5 w-3.5" />
                              Désactiver
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="h-3.5 w-3.5 text-ok" />
                              Activer
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function EnrollmentsTab() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const pending = useQuery({
    queryKey: queryKeys.adminEnrollments('pending'),
    queryFn: () => fetchers.adminEnrollments('pending'),
  });

  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      api.post(`/admin/enrollments/${id}/${action}`, {}),
    onSuccess: (_data, variables) => {
      setMessage({
        tone: 'success',
        text:
          variables.action === 'approve'
            ? "Inscription approuvée. Le candidat accède au parcours."
            : 'Inscription refusée. Elle ne sera pas redemandée.',
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'enrollments'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
    onError: (error) =>
      setMessage({
        tone: 'danger',
        text: error instanceof ApiError ? error.message : 'Action impossible.',
      }),
  });

  const rows = pending.data ?? [];

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-fg">Inscriptions en attente</h2>
        <p className="text-sm text-muted">
          Un candidat arrivant depuis EFILearn obtient un compte et voit le catalogue, mais
          n&apos;ouvre aucun contenu tant que son inscription n&apos;est pas approuvée ici.
        </p>
      </header>

      {message ? (
        <p
          role="status"
          className={
            message.tone === 'success'
              ? 'rounded-md bg-success/10 px-3 py-2 text-sm text-success'
              : 'rounded-md bg-danger/10 px-3 py-2 text-sm text-danger'
          }
        >
          {message.text}
        </p>
      ) : null}

      {pending.isLoading ? <p className="text-sm text-muted">Chargement…</p> : null}

      {!pending.isLoading && rows.length === 0 ? (
        <p className="rounded-md border border-border px-3 py-6 text-center text-sm text-muted">
          Aucune inscription en attente.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">
              Inscriptions en attente d&apos;approbation, de la plus ancienne à la plus récente
            </caption>
            <thead>
              <tr className="border-b border-border text-left text-muted">
                <th scope="col" className="py-2 pr-4 font-medium">Candidat</th>
                <th scope="col" className="py-2 pr-4 font-medium">Adresse</th>
                <th scope="col" className="py-2 pr-4 font-medium">Parcours</th>
                <th scope="col" className="py-2 pr-4 font-medium">Décision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const enCours = decide.isPending && decide.variables?.id === row.id;
                return (
                  <tr key={row.id} className="border-b border-border/60">
                    <td className="py-2 pr-4 font-medium text-fg">{row.user_full_name ?? '—'}</td>
                    <td className="py-2 pr-4 text-muted">{row.user_email ?? '—'}</td>
                    <td className="py-2 pr-4 tabular-nums">{row.track_code ?? '—'}</td>
                    <td className="py-2 pr-4">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={enCours}
                          onClick={() => decide.mutate({ id: row.id, action: 'approve' })}
                          className="rounded-md bg-success/10 px-2 py-1 text-xs font-medium text-success
                                     hover:bg-success/20 disabled:opacity-50"
                        >
                          Approuver
                        </button>
                        <button
                          type="button"
                          disabled={enCours}
                          onClick={() => decide.mutate({ id: row.id, action: 'reject' })}
                          className="rounded-md bg-danger/10 px-2 py-1 text-xs font-medium text-danger
                                     hover:bg-danger/20 disabled:opacity-50"
                        >
                          Refuser
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}


function ResultsTab() {
  const results = useQuery({ queryKey: queryKeys.adminResults, queryFn: fetchers.adminResults });

  if (results.isLoading) return <FullPageSpinner label="Chargement des résultats…" />;
  const rows = results.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Résultats des candidats ({rows.length})</CardTitle>
      </CardHeader>
      <CardBody className="p-0">
        {rows.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted">Aucun candidat enregistré.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Candidat</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Groupes</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Chapitres</th>
                  <th className="px-4 py-2.5 text-right font-semibold">TP</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Quiz réussis</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Score moyen</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Progression</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.user_id} className="border-t border-border">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-fg">{row.full_name}</p>
                      <p className="text-xs text-faint">{row.email}</p>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted">
                      {row.group_names.length > 0 ? row.group_names.join(', ') : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">
                      {row.chapters_completed}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">
                      {row.labs_completed}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">
                      {row.quizzes_passed}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">
                      {formatPercent(row.average_score_percent)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-fg">
                      {row.overall_percent} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function AuditTab() {
  const audit = useQuery({ queryKey: queryKeys.adminAudit, queryFn: fetchers.adminAudit });

  if (audit.isLoading) return <FullPageSpinner label="Chargement du journal…" />;
  const entries = audit.data ?? [];

  return (
    <div className="space-y-6">
      <Alert tone="info" title="Traçabilité">
        Toutes les opérations administratives et les consultations de corrections formateur sont
        enregistrées côté serveur, avec acteur, cible, horodatage et adresse IP.
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle as="h2" className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-ai" />
            Journal d'audit ({entries.length} dernières entrées)
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {entries.length === 0 ? (
            <p className="px-5 py-6 text-sm text-muted">Aucune entrée enregistrée.</p>
          ) : (
            <ul className="divide-y divide-border">
              {entries.map((entry) => (
                <li key={entry.id} className="px-5 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm">
                        <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-2xs text-ai">
                          {entry.action}
                        </code>
                        {entry.outcome !== 'success' ? (
                          <Badge tone="danger">{entry.outcome}</Badge>
                        ) : null}
                      </p>
                      <p className="mt-1 text-xs text-muted">
                        {entry.actor_email ?? 'anonyme'}
                        {entry.target_type ? ` → ${entry.target_type}:${entry.target_id}` : ''}
                      </p>
                    </div>
                    <p className="shrink-0 text-2xs text-faint">
                      {formatDateTime(entry.created_at)}
                      {entry.ip_address ? ` · ${entry.ip_address}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
